import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Stack,
  useTheme,
  Box,
  Button,
  ButtonGroup,
  Menu,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { ThemeProvider } from "styled-components";
import { getConsolidatedView } from "../../api/adminViewConsolidateApi";
import { getPlannerView } from "../../api/plannerViewApi";
import { AdminConsolidatedViewReponse as ConsolidatedViewRow } from "../../types/adminViewConsolidateResponse";
import { RenderTable } from "../common/splitview-table/RenderTable";
import { formatTwoDecimals, formatThreeDecimals, formatOneDecimal, formatValueNoDecimals, formatPercentNoDecimals } from "../utils/formatTwoDecimals";
import { gridTheme } from "../../utils/tableUtils";
import { formControlStyle } from "../../utils/theme";
import DownloadButton from "../common/download/DownloadIconMenu";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedDropdownValue, setSelectedRow } from "../../redux/actions/supportTicket";
import Download from "../common/DownloadGrid/Download";
import FormController from "../common/FormController/FormController";
import ButtonComponent from "../common/button/Button";
import PageChangeWarningDialog from "../common/PageChangeWarningDialog";
import { setApiCall } from "../../redux/actions/userSlice";
import BulkPasteFilter from "../common/BulkFilter/BulkFilter";
import FilterManagerButton from "../common/FilterVariant/FilterManagerButton";
const normalizePlannerRow = (plannerRow: any): any => {
  const src = plannerRow.safetyStockSource;
  const approver = plannerRow.approvers?.[0] || {};

  return {
    workflowId: null,
    materialNumber: src.materialNumber,
    materialPlant: src.materialPlant,
    materialDescription: src.materialDescription,
    region: src.region,
    userId: null,
    userName: null,
    approverId: approver.approverId || null,
    approverName: approver.approverName || null,
    userSafetyTime: 0,
    userSafetyStockQty: plannerRow.ioSafetyStockQty || 0,
    userCoverageProfile: 0,
    userConsolidatedSafetyStock: 0,
    varWkUserVsS4: 0,
    varPctUserVsS4: "0",
    userSelectedChoice: null,
    userComments: null,
    s4SafetyStockQty: src.s4SafetyStockQty || "0",
    s4CoverageProfile: src.s4CoverageProfile || "0",
    s4SafetyTime: src.s4SafetyTime || "0",
    ioCoverageProfile: src.ioCoverageProfile || 0,
    ioSafetyTime: src.ioSafetyTime || 0,
    approvedConsolidatedSafetyStock: 0,
    approvedCoverageProfile: 0,
    approvedSafetyTime: 0,
    approvedSafetyStockQty: 0,
    varWkApprovedSSvsS4: 0,
    varPctApprovedSSvsS4: "0",
    approverSelectedChoice: null,
    approverComments: null,
    approverReasonCode: plannerRow.reasonCode || "",
    consolidatedSafetyStockUsdApproved: 0,
    varUsdApprovedSSvsS4: 0,
    varPdrApprovedSSvsS4: 0,
    mrpControllerId: src.mrpControllerId ?? null,
    ioMaterialType: src.ioMaterialType || "",
    mrpControllerDescription: src.mrpControllerDescription || "",
    ioConsolidatedSafetyStockRounded: src.ioConsolidatedSafetyStockRounded ?? 0,
    s4ConsolidatedSafetyStock: src.s4ConsolidatedSafetyStock ?? 0,
    ioExtractionDate: src.ioExtractionDate || "",
    ioRunTypeLabel: src.ioRunTypeLabel || "",
    weeklyDemandQty: src.weeklyDemandQty ?? 0,
    partUnitsPerEa: src.partUnitsPerEa ?? 0,
    writebackDate: src.writebackDate || "",
    writebackStatus: src.writebackStatus || "",
    approval_required: src.approval_required || "",
    reviewRequired:src.reviewRequired||"",
    planningLead: src.planningLead||"",
    lineStatus: plannerRow.lineStatus || "",
    pricePerUnit: src.pricePerUnit ?? 0,
    exchangeRate: src.exchangeRate ?? 0,
    standardPrice: src.standardPrice ?? 0,
    ssFinal: plannerRow.ssFinal,
    currency: plannerRow.currency
  };
};

const parseNumberOrText = (value: any) => {
  if (value === null || value === undefined) return "";
  const v = String(value).trim();
  if (v === "-" || v === "") return "";
  const num = Number(v);
  return Number.isFinite(num) ? num : v; // allow text if needed
};
type TableRow = any;
export const AdminViewConsolidate = () => {
  const theme = useTheme();
  const [currency, setCurrency] = useState<"USD" | "LOCAL">("USD");
  const [selectedCount, setSelectedCount] = useState(0);
  const [source, setSource] = useState<TableRow[]>([]); // State for the fetched data
  const [loading, setLoading] = useState(false);
  const [isTriggerFilter, setIsTriggerFilter] = useState(false);
  const gridRef = useRef<any>(null);
  const selectedRowIdsRef = useRef<Set<string>>(new Set());
  const dropdownValuesSetRef = useRef(false);
  const prevPageRef = useRef<number | null>(null);
  const pendingPageRef = useRef<number | null>(null);
  const suppressPaginationRef = useRef(false);
  const freezeSelectionRef = useRef(false);
  const selectionSnapshotRef = useRef<Set<string>>(new Set());
  const [openPageChangeWarning, setOpenPageChangeWarning] = useState(false);
  const lastFetchedPlantKeyRef = useRef<string | null>(null);
    const restoreSelectionSnapshot = useCallback(() => {
      const api = gridRef.current?.api;
      if (!api) return;
      const snapshot = selectionSnapshotRef.current;
      if (!snapshot.size) return;

      api.forEachNode((node: any) => {
        const nodeId = String(node.id);
        node.setSelected(snapshot.has(nodeId));
      });
      setSelectedCount(snapshot.size);
    }, []);

  const dispatch = useDispatch();
  const selectedPlant = useSelector((state: any) => state?.userRoles?.selectedPlant || []);
 
  const { isSelectionRemove } = useSelector(
    (state: any) => state?.supportTicket || {},
  );
  const getBusinessKey = (row: any) =>
    `${row.materialNumber}__${row.materialPlant}__${row.ioExtractionDate ?? "NA"}`;
 
  // Fetch data from API
  const fetchData = async () => {
      setLoading(true);
      dispatch(setApiCall(true))
      try {
        const [consolidatedData, plannerData] = await Promise.all([
          getConsolidatedView(),
          getPlannerView("5040"),
        ]);
        //  Normalize planner rows
        const normalizedPlannerData = plannerData.map(normalizePlannerRow);

        // Merge both datasets
        const merged = [...consolidatedData, ...normalizedPlannerData];

        // De-duplicate using business key
        const map = new Map<string, any>();

        merged.forEach((row) => {
          const key = getBusinessKey(row);

          // Prefer consolidated row over planner row if duplicate exists
          if (!map.has(key) || row.workflowId) {
            map.set(key, row);
          }
        });

        // Final array with STABLE row id
        const finalRows = Array.from(map.values()).map((row, index) => ({
          ...row,
          _rowId: `${getBusinessKey(row)}__${index}`,
          __uid: `${getBusinessKey(row)}__${index}`,
        }));

        setSource(finalRows);
      } catch (error) {
        console.error("Error fetching data", error);
      } finally {
         dispatch(setApiCall(false))
        setLoading(false);
      }
    };
useEffect(() => {
  // If selectedPlant is empty, clear the table data
  if (!selectedPlant || selectedPlant.length === 0) {
    setSource([]);
    lastFetchedPlantKeyRef.current = null;
    return;
  }

  const plantKey = selectedPlant.join(",");
  if (lastFetchedPlantKeyRef.current === plantKey) {
    return;
  }

  lastFetchedPlantKeyRef.current = plantKey;
  fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedPlant]);
  // Function to convert key names to camelCase for fields
  const toFieldName = (key: string) => {
    if (!key) return key;
    const parts = key.split(" ");
    parts[0] = parts[0].charAt(0).toLowerCase() + parts[0].slice(1);
    return parts.join("").replace(/[^\w]/g, "");
  };

  const handleCurrencyChange = (event) => setCurrency(event.target.value as "USD" | "LOCAL");

  // Re-evaluate filters after currency-converted row values are applied
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [currency]);

  // Maps currency code from API (e.g. "EUR") to its symbol using the browser Intl API
  const getCurrencySymbol = (code: string): string => {
    if (!code) return "";
    try {
      return (0)
        .toLocaleString("en", {
          style: "currency",
          currency: code,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
        .replace(/[\d,\s.]/g, "")
        .trim();
    } catch {
      return code;
    }
  };

  // Define columns dynamically based on the first row of the fetched data
  const columns = useMemo(() => {
    const existingCols: any[] = [
      'Extraction Date',
      'IO Run Type',
      "Approval status",
      "MRP controller",
      "MRP controller desc",
      "BCP Flag",
      "Material Plant",
      "Planning Lead",
      "IO Material Type",
      "Material Number",
      "Material Description",
      "Approval Required?",
      "Review Flag for IO Writeback",
      "Approver",
      "Standard Price",
      "Price Unit",
      "Exchange Rate",
      "Part Units Per EA",
      "Weekly Demand Qty",
      "SS Final (weeks)",
      // Group: S4
      {
        groupHeaderName: "S4",
        marryChildren: true,
        children: [
          "S4 Safety Stock (qty)",
          "S4 Safety Time (weeks)",
          "S4 Coverage Profile (weeks)",
          "S4 consolidated SS (weeks)",
        ],
      },
      // Group: IO
      {
        groupHeaderName: "IO",
        marryChildren: true,
        children: [
          "IO Safety Time (weeks)",
          "IO Coverage Profile (weeks)",
          "IO consolidated SS (weeks)",
        ],
      },
      // Group: End User proposal
      {
        groupHeaderName: "End User proposal",
        marryChildren: true,
        children: [
          "End User action",
          "Reason",
          "Comments",
          "Total consolidated Safety Stock (weeks)-EU",
          "Safety Time (weeks)-EU",
          "Coverage Profile (weeks)-EU",
          "Safety Stock (qty)-EU",
        ],
      },
      // Group: Approved Safety Stock
      {
        groupHeaderName: "Approved Safety Stock",
        marryChildren: true,
        children: [
          "Final selected choice",
          "Total consolidated Safety Stock (weeks)-approved",
          "Safety Time (weeks)-approved",
          "Coverage Profile (weeks)-approved",
          "Safety Stock (qty)-approved",
          "Comments from the approver",
          "Reason Approver",
          "IO SS value",
          "S4 SS value",
          "Approved SS value",
        ],
      },
      // Other fields
      "VAR (value) - Approved SS value vs S4",
      "VAR (value) - Approved SS vs IO",
      "VAR (PDR) - Approved value vs S4",
      "VAR (%) - Approved SS vs S4(weeks)",
      "VAR (%) - Approved SS vs IO(weeks)",
      // "Currency",
    ];

    // Columns whose cell values are numeric (raw numbers stored in obj)
    const numericHeaders = new Set([
      "S4 Safety Stock (qty)",
      "S4 Safety Time (weeks)",
      "S4 Coverage Profile (weeks)",
      "S4 consolidated SS (weeks)",
      "IO Safety Time (weeks)",
      "IO Coverage Profile (weeks)",
      "IO consolidated SS (weeks)",
      "SS Final (weeks)",
      "Total consolidated Safety Stock (weeks)-EU",
      "Safety Time (weeks)-EU",
      "Coverage Profile (weeks)-EU",
      "Safety Stock (qty)-EU",
      "Total consolidated Safety Stock (weeks)-approved",
      "Safety Time (weeks)-approved",
      "Coverage Profile (weeks)-approved",
      "Safety Stock (qty)-approved",
      "Standard Price",
      "Price Unit",
      "Exchange Rate",
      "Part Units Per EA",
      "Weekly Demand Qty",
      "VAR (value) - Approved SS value vs S4",
      "VAR (value) - Approved SS vs IO",
      "VAR (PDR) - Approved value vs S4",
      "VAR (%) - Approved SS vs S4(weeks)",
      "VAR (%) - Approved SS vs IO(weeks)",
      "IO SS value",
      "S4 SS value",
      "Approved SS value",
    ]);

    // Helper function to ensure column exists or create it if missing
    const ensureCol = (header: string): any => {
      const field = toFieldName(header);

      const colDef: any = { field, headerName: header };

      if (header === "MRP controller" || header === "Material Plant") {
        colDef.cellDataType = "text";
      }

      if (header === "Extraction Date") {
        colDef.comparator = (a: any, b: any) => {
          const toTime = (v: any) => {
            if (!v || v === "-") return 0;
            const t = new Date(v).getTime();
            return isNaN(t) ? 0 : t;
          };
          return toTime(a) - toTime(b);
        };
      }

      if (numericHeaders.has(header)) {
        colDef.filter = "agMultiColumnFilter";
        colDef.filterParams = {
          filters: [
            {
              filter: "agNumberColumnFilter",
              filterParams: { buttons: ["reset", "apply"], closeOnApply: true },
            },
            {
              filter: "agSetColumnFilter",
              // Override valueGetter so the Set dropdown shows the actual
              // formatted cell value (e.g. "$1,234", "25%", "N/A") instead of
              // the stripped number returned by the default filterValueGetter.
              // No Apply button: checkboxes apply immediately, same as Enter in search.
              filterParams: {
                buttons: ["reset"],
                valueGetter: (params: any) => {
                  const val = params.data?.[field];
                  if (val === null || val === undefined || val === "") return null;
                  return String(val);
                },
              },
            },
          ],
        };
      }

      return colDef;
    };

    const columnDefs: any[] = [];
    existingCols.forEach((entry) => {
      if (typeof entry === "string") {
        columnDefs.push(ensureCol(entry));
      } else {
        const { groupHeaderName, children, marryChildren = true } = entry;
        const childDefs = children.map((h: string) => ensureCol(h));
        columnDefs.push({
          headerName: groupHeaderName,
          marryChildren,
          children: childDefs,
        });
      }
    });

    return columnDefs;
  }, []);

  // Prepare rows dynamically from the API response using switch-case
  const rows = useMemo(() => {
    return source.map((computedRow, idx) => {
      const row = { ...computedRow }; //  ADD THIS LINE
      const obj: any = { ...row };
      obj.__uid = row.__uid ?? `${row.materialNumber ?? "mat"}__${idx}`;
      const showVarFields = row.lineStatus === "End User SS Approved" || row.lineStatus === "End User SS Rejected";

      //calculation
      //VAR (value) - Approved SS value vs S4
      //formula : [Consolidated Safety Stock (USD) - Approved]-[Consolidated Safety Stock (USD) - S4]
      //formula Consolidated Safety Stock (USD) - Approved : =[Consolidated Safety Stock (wk) - Approved]*[Weekly Demand (Qty)]*[Std Price (USD/Qty)]
      //formula Consolidated Safety Stock (USD) - S4 = [S4 Consolidated Safety Stock (wk)]*[Weekly Demand (Qty)]*[Std Price (USD/Qty)]
      const pricePerUnit = Number(parseNumberOrText(row?.["pricePerUnit"]));
      const exchangeRate = Number(parseNumberOrText(row?.["exchangeRate"]));
      const stdPricePerQty = pricePerUnit <= 0 ? 0 :
        currency === "LOCAL" ? row.standardPrice / pricePerUnit : exchangeRate >= 0 ?
          row.standardPrice * exchangeRate / pricePerUnit : row.standardPrice / (pricePerUnit * exchangeRate);
      const weeklyDemand = Number(parseNumberOrText(row?.["weeklyDemandQty"]));
      const s4ConsolidatedSafetyStock = Number(
        parseNumberOrText(row?.["s4ConsolidatedSafetyStock"]),
      );
      const consolidatedSafetyStockApproved = Number(
        parseNumberOrText(row?.["approvedConsolidatedSafetyStock"]),
      );
      row["consolidatedSafetyStockUsdApproved"] =
        consolidatedSafetyStockApproved * weeklyDemand * stdPricePerQty;
      row["consolidatedSafetyStockUsdS4"] =
        s4ConsolidatedSafetyStock * weeklyDemand * stdPricePerQty;
      row["varUsdApprovedSSvsS4"] =
        row["consolidatedSafetyStockUsdApproved"] - row["consolidatedSafetyStockUsdS4"];

      // IO Consolidated Safety Stock (wk) - Rounded
      const ioConsolidatedSafetyStockRounded =
        Number(parseNumberOrText(row?.["ioConsolidatedSafetyStockRounded"])) || 0;

      // Consolidated Safety Stock (USD) - IO
      row["consolidatedSafetyStockUsdIO"] =
        ioConsolidatedSafetyStockRounded * weeklyDemand * stdPricePerQty;

      // VAR (USD) – Approved SS vs IO 
      row["varUsdApprovedSSvsIO"] =
        row["consolidatedSafetyStockUsdApproved"] - row["consolidatedSafetyStockUsdIO"];

      //VAR (%) - Approved SS vs S4
      //formula= =IF([S4 Consolidated Safety Stock (wk)]=0,0,[VAR (wk) - Approved SS vs S4]/[S4 Consolidated Safety Stock (wk)])
      const varWkApprovedVsS4 = consolidatedSafetyStockApproved - s4ConsolidatedSafetyStock;

      // Calculate the VAR (%) for Approved SS vs S4
      row["varPctApprovedSSvsS4_isNA"] = s4ConsolidatedSafetyStock === 0;
      row["varPctApprovedSSvsS4"] =
        s4ConsolidatedSafetyStock === 0
          ? 0
          : (varWkApprovedVsS4 / s4ConsolidatedSafetyStock) * 100;

      //VAR (%) - Approved SS vs IO
      //formula= IF([IO Consolidated Safety Stock (wk) - Rounded] = 0 ,0, [VAR (wk) - Approved SS vs IO]/[IO Consolidated Safety Stock (wk) - Rounded])
      //VAR (wk) - Approved SS vs IO [Consolidated Safety Stock (wk) - Approved]-[IO Consolidated Safety Stock (wk) - Rounded]
      const varWkApprovedVsIO =
        consolidatedSafetyStockApproved - ioConsolidatedSafetyStockRounded;

      // VAR (%) - Approved SS vs IO 
      row["varPctApprovedSSvsIO_isNA"] = ioConsolidatedSafetyStockRounded === 0;
      row["varPctApprovedSSvsIO"] =
        ioConsolidatedSafetyStockRounded === 0
          ? 0
          : (varWkApprovedVsIO * 100) / ioConsolidatedSafetyStockRounded;

      //VAR (PDR) - Approved SS vs S4
      //formula= =[VAR (wk) - Approved SS vs S4]*[Weekly Demand (Qty)]*[Part Units pear EA (PDR/EA)]
      const partUnitsPerEA = Number(
        parseNumberOrText(row?.["partUnitsPerEa"]),
      );
      row["varPdrApprovedSSvsS4"] =
        varWkApprovedVsS4 * weeklyDemand * partUnitsPerEA;

      // Approved SS value
      const currencySymbol = currency === "USD" ? "$" : getCurrencySymbol(row.currency ?? "");
      row["approvedSSValue"] =
        formatValueNoDecimals(
          consolidatedSafetyStockApproved * weeklyDemand * stdPricePerQty,
          currencySymbol,
        );
      row["ioSsValue"] =
        formatValueNoDecimals(
          ioConsolidatedSafetyStockRounded * weeklyDemand * stdPricePerQty,
          currencySymbol,
        );
      row["s4SsValue"] =
        formatValueNoDecimals(
          s4ConsolidatedSafetyStock * weeklyDemand * stdPricePerQty,
          currencySymbol,
        );

      columns.forEach((col: any) => {
        // If the column is grouped, process its children
        if (col.children) {
          col.children.forEach((child: any) => {
            switch (child.headerName) {
              // Handle grouped fields as before
              case "Approval status":
                obj[child.field] = row.lineStatus || "";
                break;
              case "Extraction Date":
                obj[child.field] = row.ioExtractionDate || "";
                break;
              case 'IO Run Type':
                obj[child.field] = row.ioRunTypeLabel || '';
                break;
              case "MRP controller":
                obj[child.field] = row.mrpControllerId || "";
                break;
              case "MRP controller desc":
                obj[child.field] = row.mrpControllerDescription || "";
                break;
              case "BCP Flag":
                obj[child.field] = row.bpcFlag || "";
                break;
              case "Material Plant":
                obj[child.field] = row.materialPlant || "";
                break;
              case "Planning Lead":
                obj[child.field] = row.planningLead ?? "";
                break;
              case "IO Material Type":
                obj[child.field] = row.ioMaterialType || "";
                break;
              case "Material Number":
                obj[child.field] = row.materialNumber || "";
                break;
              case "Material Description":
                obj[child.field] = row.materialDescription || "";
                break;
              case "Approval Required?":
                obj[child.field] = row.approval_required;
                break;
              case "Review Flag for IO Writeback":
                obj[child.field] = row.reviewRequired ?? "";
                break;
              case "Approver":
                obj[child.field] = row.approverName || "";
                break;
              // Handle group-specific fields like S4, IO, End User proposal, Approved Safety Stock, etc.
              case "S4 Safety Stock (qty)":
                obj[child.field] = formatValueNoDecimals(row.s4SafetyStockQty ?? 0);
                break;
              case "S4 Safety Time (weeks)":
                obj[child.field] = formatOneDecimal(row.s4SafetyTime ?? "0");
                break;
              case "S4 Coverage Profile (weeks)":
                obj[child.field] = formatOneDecimal(
                  row.s4CoverageProfile ?? "0",
                );
                break;
              case "S4 consolidated SS (weeks)":
                obj[child.field] = formatOneDecimal(
                  row.s4ConsolidatedSafetyStock ?? "0",
                );
                break;
              case "IO Safety Time (weeks)":
                obj[child.field] = formatOneDecimal(
                  row.ioSafetyTime ?? "0",
                );
                break;
              case "IO Coverage Profile (weeks)":
                obj[child.field] = formatOneDecimal(
                  row.ioCoverageProfile ?? "0",
                );
                break;
              case "IO consolidated SS (weeks)":
                obj[child.field] = formatOneDecimal(
                  row.ioConsolidatedSafetyStockRounded ?? "0",
                );
                break;
              case "End User action":
                obj[child.field] = row.userSelectedChoice || "";
                break;
              case "Reason":
                obj[child.field] = row.userReasonCode || "";
                break;
              case "Comments":
                obj[child.field] = row.userComments || "";
                break;
              case "Total consolidated Safety Stock (weeks)-EU":
                obj[child.field] = formatOneDecimal(
                  row.userConsolidatedSafetyStock ?? 0,
                );
                break;
              case "Safety Time (weeks)-EU":
                obj[child.field] = formatOneDecimal(row.userSafetyTime ?? "0");
                break;
              case "Coverage Profile (weeks)-EU":
                obj[child.field] = formatOneDecimal(
                  row.userCoverageProfile ?? "0",
                );
                break;
              case "Safety Stock (qty)-EU":
                obj[child.field] = formatValueNoDecimals(
                  row.userSafetyStockQty ?? "0",
                );
                break;
              case "Final selected choice":
                obj[child.field] = row.approverSelectedChoice || "";
                break;
              case "Total consolidated Safety Stock (weeks)-approved":
                obj[child.field] = formatOneDecimal(
                  row.approvedConsolidatedSafetyStock ?? 0,
                );
                break;
              case "Safety Time (weeks)-approved":
                obj[child.field] = formatOneDecimal(
                  row.approvedSafetyTime ?? 0,
                );
                break;
              case "Coverage Profile (weeks)-approved":
                obj[child.field] = formatOneDecimal(
                  row.approvedCoverageProfile ?? 0,
                );
                break;
              case "Safety Stock (qty)-approved":
                obj[child.field] = formatValueNoDecimals(
                  row.approvedSafetyStockQty ?? 0,
                );
                break;
              case "Comments from the approver":
                obj[child.field] = row.approverComments || "";
                break;
              case "Reason Approver":
                obj[child.field] = row.approverReasonCode || "";
                break;
              case "Weekly Demand Qty":
                obj[child.field] = formatTwoDecimals(row.weeklyDemandQty ?? 0);
                break;
              case "Approved SS value":
                obj[child.field] = row.approvedSSValue || "-";
                break;
              case "IO SS value":
                obj[child.field] = row.ioSsValue || "-";
                break;
              case "S4 SS value":
                obj[child.field] = row.s4SsValue || "-";
                break;
              // VAR fields

              default:
                obj[child.field] = row[child.field] || "";
                break;
            }
          });
        } else {
          // Handle non-grouped columns (these don't have `children`)
          switch (col.headerName) {
            case "Approval status":
              obj[col.field] = row.lineStatus || "";
              break;
            case "Extraction Date":
              obj[col.field] = row.ioExtractionDate || "";
              break;
            case 'IO Run Type':
              obj[col.field] = row.ioRunTypeLabel || '';
              break;
            case "MRP controller":
              obj[col.field] = row.mrpControllerId || "";
              break;
            case "MRP controller desc":
              obj[col.field] = row.mrpControllerDescription || "";
              break;
            case "BCP Flag":
              obj[col.field] = row.bpcFlag || "";
              break;
            case "Material Plant":
              obj[col.field] = row.materialPlant || "";
              break;
            case "Planning Lead":
              obj[col.field] = row.planningLead ?? "";
              break;
            case "IO Material Type":
              obj[col.field] = row.ioMaterialType || "";
              break;
            case "Material Number":
              obj[col.field] = row.materialNumber || "";
              break;
            case "Material Description":
              obj[col.field] = row.materialDescription || "";
              break;
            case "Approval Required?":
              obj[col.field] = row.approval_required;
              break;
            case "Review Flag for IO Writeback":
              obj[col.field] = row.reviewRequired ?? "";
              break;
            case "Approver":
              obj[col.field] = row.approverName || "";
              break;
            case "Approved SS value":
              obj[col.field] = row.approvedSSValue || "-";
              break;
            case "IO SS value":
              obj[col.field] = row.ioSsValue || "-";
              break;
            case "S4 SS value":
              obj[col.field] = row.s4SsValue || "-";
              break;
            case "Standard Price":
              obj[col.field] = currency === "USD"
                ? "$" + formatValueNoDecimals((row.standardPrice ?? 0) * (row.exchangeRate ?? 0))
                : getCurrencySymbol(row.currency ?? "") + formatValueNoDecimals(row.standardPrice ?? 0);
              break;
            case "Price Unit":
              obj[col.field] = formatValueNoDecimals(row.pricePerUnit ?? 0);
              break;
            case "Exchange Rate":
              obj[col.field] = formatThreeDecimals(row.exchangeRate ?? 0);
              break;
            case "Part Units Per EA":
              obj[col.field] = formatOneDecimal(row.partUnitsPerEa ?? 0);
              break;
            case "Weekly Demand Qty":
              obj[col.field] = formatOneDecimal(row.weeklyDemandQty ?? 0);
              break;
            case "SS Final (weeks)":
              obj[col.field] = formatOneDecimal(row.ssFinal ?? "0");
              break;
            case "VAR (value) - Approved SS value vs S4":
              obj[col.field] = showVarFields ? formatValueNoDecimals(
                row.varUsdApprovedSSvsS4 || 0,
                currency === "USD" ? "$" : getCurrencySymbol(row.currency ?? ""),
              ) : "";
              break;
            case "VAR (value) - Approved SS vs IO":
              obj[col.field] = showVarFields ? formatValueNoDecimals(
                row.varUsdApprovedSSvsIO || 0,
                currency === "USD" ? "$" : getCurrencySymbol(row.currency ?? ""),
              ) : "";
              break;
            case "VAR (PDR) - Approved value vs S4":
              obj[col.field] = showVarFields ? formatValueNoDecimals(
                row.varPdrApprovedSSvsS4 || "0",
              ) : "";
              break;
            case "VAR (%) - Approved SS vs S4(weeks)":
              obj[col.field] = showVarFields
                ? (row.varPctApprovedSSvsS4_isNA
                    ? "N/A"
                    : formatValueNoDecimals(row.varPctApprovedSSvsS4 ?? 0) + "%")
                : "";
              break;
            case "VAR (%) - Approved SS vs IO(weeks)":
              obj[col.field] = showVarFields
                ? (row.varPctApprovedSSvsIO_isNA
                    ? "N/A"
                    : formatValueNoDecimals(row.varPctApprovedSSvsIO ?? 0) + "%")
                : "";
              break;
            default:
              obj[col.field] = row[col.field] || "";
              break;
          }
        }
      });
      return obj;
    });
  }, [source, columns, currency]);



  const DeSelectItems = () => {
    const api = gridRef.current?.api;
    if (api) api.deselectAll();
    selectedRowIdsRef.current = new Set();
    dropdownValuesSetRef.current = false;
    setSelectedCount(0);
  };

  useEffect(() => {
    if (openPageChangeWarning) return;
    if (!isSelectionRemove) return;
    DeSelectItems();
  }, [isSelectionRemove, openPageChangeWarning]);

  const handleSelectionChanged = useCallback(
    (event: any) => {
      if (freezeSelectionRef.current) return;
      const nodes = event.api.getSelectedNodes();
      selectedRowIdsRef.current = new Set(nodes.map((n: any) => String(n.id)));
      setSelectedCount(nodes.length);
      dispatch(setSelectedRow(nodes.map((n: any) => n.data)));

      // Set default dropdown values only once when rows are first selected
      if (nodes.length > 0 && !dropdownValuesSetRef.current) {
        dropdownValuesSetRef.current = true;
        dispatch(
          setSelectedDropdownValue({
            menuId: 'SSA Dashboard',
            categoryId: "Data",
            functionId: "Data",
          }),
        );
      }

      // Reset the flag when selection is cleared
      if (nodes.length === 0) {
        dropdownValuesSetRef.current = false;
      }
    },
    [dispatch],
  );

  const handlePageChange = useCallback(
    (e?: any) => {
      if (suppressPaginationRef.current) return;
      const api = gridRef?.current?.api;
      if (!api) return;

      const current = api.paginationGetCurrentPage?.() ?? 0;
      const prev = prevPageRef.current;

      // Initialize on first event
      if (prev === null) {
        prevPageRef.current = current;
        return;
      }

      if (current !== prev) {
        if (selectedRowIdsRef.current.size > 0) {
          freezeSelectionRef.current = true;
          selectionSnapshotRef.current = new Set(selectedRowIdsRef.current);
          suppressPaginationRef.current = true;
          api.paginationGoToPage(prev);
          suppressPaginationRef.current = false;
          pendingPageRef.current = current;
          setOpenPageChangeWarning(true);
        } else {
          api.deselectAll();
          selectedRowIdsRef.current = new Set();
          setSelectedCount(0);
          dropdownValuesSetRef.current = false;
          prevPageRef.current = current;
        }
      }
      // If current === prev, do nothing (don’t clear)
    },
    [gridRef],
  );

  const handlePageChangeConfirm = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    freezeSelectionRef.current = false;
    selectionSnapshotRef.current = new Set();
    api.deselectAll();
    selectedRowIdsRef.current = new Set();
    setSelectedCount(0);
    dropdownValuesSetRef.current = false;
    dispatch(setSelectedRow([]));

    if (pendingPageRef.current !== null) {
      suppressPaginationRef.current = true;
      api.paginationGoToPage(pendingPageRef.current);
      suppressPaginationRef.current = false;
      prevPageRef.current = pendingPageRef.current;
      pendingPageRef.current = null;
    }
    setOpenPageChangeWarning(false);
  }, [dispatch]);

  const handlePageChangeCancel = useCallback(() => {
    pendingPageRef.current = null;
    freezeSelectionRef.current = false;
    restoreSelectionSnapshot();
    setOpenPageChangeWarning(false);
  }, [restoreSelectionSnapshot]);

  const handleResetFilters = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.setFilterModel(null);
    if (typeof api.setGridOption === "function") {
      api.setGridOption("quickFilterText", "");
    }
    api.onFilterChanged();
    setIsTriggerFilter(true);
  }, []);

  return (
    <ThemeProvider theme={ theme }>
      <Stack gap={ 2 } padding={ 2 } sx={ { padding: 2, paddingLeft: 0, flex: 1 } }>
        <Stack
          direction="row"
          gap={ 2 }
          alignItems="center"
          justifyContent="space-between"
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography sx={ { fontSize: "20px", fontWeight: 600 } }>
              SSA Dashboard View
            </Typography>
          </Box>
          {/* Right-side controls */ }
          <Stack direction="row" spacing={ 1 } alignItems="center">
            <FormController currency={ currency } handleCurrencyChange={ handleCurrencyChange } />
            { (source?.length ?? 0) > 0 && (
              <><ButtonComponent
                className="take-action"
                type="secondary"
                label="Reset Filters"
                onClick={ handleResetFilters }
                width="auto"
                maxWidth="none"
                styles={ { borderRadius: 28 } }
              />
              <FilterManagerButton    
                             gridRef={gridRef}    
                             tableName="SSA Dashboard View"    
                             userEmail={localStorage.getItem("userEmail") || ""}    
                             disabled={!source?.length}  />
              <BulkPasteFilter
    gridRef={gridRef}
    columns={columns}
    triggerClearFilter = {isTriggerFilter}
    setIsTriggerFilter={setIsTriggerFilter}
  />
      </>      ) }
            


            <Download loading={ loading } gridRef={ gridRef } fileName='SSA Dashboard View' rowData={ source } />
          </Stack>
        </Stack>

        <RenderTable
          loading={ loading }
          gridRef={ gridRef }
          columns={ columns } // Passing dynamic columns
          gridTheme={ gridTheme }
          rows={ rows } // Passing dynamic rows
          rowsPerPage={ 10 }
          setGridReady={ () => { } }
          sideBar={ {} }
          minimized={ null }
          emitEvent={ () => { } }
          onChange={ () => { } }
          setSelectedRow={ () => { } }
          setOpenRejectPopup={ () => { } }
          withSelectionColumn={ true }
          selectionScope="page"
          onSelectionChanged={ handleSelectionChanged }
          handleChangePage={ handlePageChange }
          selectedCount={ selectedCount }
        />

        <Box paddingBottom={ 1 }></Box>
        <PageChangeWarningDialog
          open={openPageChangeWarning}
          selectedCount={selectedCount}
          onCancel={handlePageChangeCancel}
          onConfirm={handlePageChangeConfirm}
        />
      </Stack>
    </ThemeProvider>
  );
};
